import React, { useMemo, memo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,

} from 'recharts';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Paper } from '@mui/material';
import { ExpenseData } from '../../../../../backend/src/types';

export interface ExpenseChartProps {
  data: ExpenseData[];
  chartType?: 'pie' | 'doughnut' | 'bar';
  groupBy?: 'category' | 'property' | 'time';
  height?: number;
  showControls?: boolean;
  onChartTypeChange?: (type: 'pie' | 'doughnut' | 'bar') => void;
  onGroupByChange?: (groupBy: 'category' | 'property' | 'time') => void;
}

interface ChartDataPoint {
  name: string;
  value: number;
  count: number;
  percentage: number;
  details: Array<{
    description: string;
    amount: number;
    date: string;
  }>;
}

const COLORS = [
  '#1976d2', '#dc004e', '#9c27b0', '#673ab7', '#3f51b5',
  '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
  '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
  '#ff5722', '#795548', '#9e9e9e', '#607d8b'
];

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CustomTooltip: React.FC<any> = ({ active, payload, label, chartType, showDetails = true }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as ChartDataPoint;
    
    return (
      <Paper sx={{ p: 2, maxWidth: 300 }}>
        <Typography variant="subtitle2" gutterBottom>
          {data.name}
        </Typography>
        <Typography variant="body2" color="primary">
          Amount: {formatCurrency(data.value)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Percentage: {data.percentage.toFixed(1)}%
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Transactions: {data.count}
        </Typography>
        
        {showDetails && data.details.length > 0 && data.details.length <= 5 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Recent expenses:
            </Typography>
            {data.details.slice(0, 3).map((detail, index) => (
              <Typography key={index} variant="caption" display="block">
                {formatDate(detail.date)}: {formatCurrency(detail.amount)}
                {detail.description && ` - ${detail.description.substring(0, 30)}${detail.description.length > 30 ? '...' : ''}`}
              </Typography>
            ))}
            {data.details.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{data.details.length - 3} more...
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  }

  return null;
};

const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  if (percent < 0.05) return null; // Don't show labels for slices less than 5%
  
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize={12}
      fontWeight="bold"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const ExpenseChart: React.FC<ExpenseChartProps> = memo(({
  data,
  chartType = 'pie',
  groupBy = 'category',
  height = 400,
  showControls = true,
  onChartTypeChange,
  onGroupByChange,
}) => {
  const chartData = useMemo(() => {
    // Handle undefined or empty data
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    // Group data based on groupBy parameter
    const groupedData = data.reduce((acc, item) => {
      let key: string;
      
      switch (groupBy) {
        case 'property':
          key = item.propertyName || 'Unknown Property';
          break;
        case 'time':
          // Group by month for time-based grouping
          const date = new Date(item.date);
          key = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          break;
        case 'category':
        default:
          key = item.category || 'Uncategorized';
          break;
      }
      
      if (!acc[key]) {
        acc[key] = {
          name: key,
          value: 0,
          count: 0,
          percentage: 0,
          details: [],
        };
      }
      
      acc[key].value += item.amount;
      acc[key].count += 1;
      acc[key].details.push({
        description: item.description || '',
        amount: item.amount,
        date: item.date,
      });
      
      return acc;
    }, {} as Record<string, ChartDataPoint>);
    
    // Calculate percentages and sort by value
    const groupedValues = Object.values(groupedData);
    const totalValue = groupedValues.length > 0 
      ? groupedValues.reduce((sum, item) => sum + item.value, 0) 
      : 0;
    const sortedData = Object.values(groupedData)
      .map(item => ({
        ...item,
        percentage: totalValue > 0 ? (item.value / totalValue) * 100 : 0,
        details: item.details.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
      }))
      .sort((a, b) => b.value - a.value);
    
    return sortedData;
  }, [data, groupBy]);

  const renderChart = () => {
    if (chartType === 'bar') {
      return (
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis 
            tickFormatter={formatCurrency}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip chartType={chartType} />} />
          <Legend />
          <Bar 
            dataKey="value" 
            name="Expenses"
            radius={[4, 4, 0, 0]}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      );
    }

    // Pie or Doughnut chart
    const innerRadius = chartType === 'doughnut' ? 60 : 0;
    
    return (
      <PieChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={CustomPieLabel}
          outerRadius={Math.min(height * 0.35, 150)}
          innerRadius={innerRadius}
          fill="#8884d8"
          dataKey="value"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip chartType={chartType} />} />
      </PieChart>
    );
  };

  const totalExpenses = chartData && chartData.length > 0 
    ? chartData.reduce((sum, item) => sum + item.value, 0) 
    : 0;
  const totalTransactions = chartData && chartData.length > 0 
    ? chartData.reduce((sum, item) => sum + item.count, 0) 
    : 0;
  const averageExpense = totalTransactions > 0 ? totalExpenses / totalTransactions : 0;

  // Handle no data case
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height }}>
        <Typography variant="body2" color="text.secondary">
          No expense data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {showControls && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexShrink: 0 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Chart Type</InputLabel>
            <Select
              value={chartType}
              label="Chart Type"
              onChange={(e) => onChartTypeChange?.(e.target.value as 'pie' | 'doughnut' | 'bar')}
            >
              <MenuItem value="pie">Pie</MenuItem>
              <MenuItem value="doughnut">Doughnut</MenuItem>
              <MenuItem value="bar">Bar</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Group By</InputLabel>
            <Select
              value={groupBy}
              label="Group By"
              onChange={(e) => onGroupByChange?.(e.target.value as 'category' | 'property' | 'time')}
            >
              <MenuItem value="category">Category</MenuItem>
              <MenuItem value="property">Property</MenuItem>
              <MenuItem value="time">Time</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}
      
      {/* Split Layout: Pie Chart Left, Category List Right */}
      <Box sx={{ display: 'flex', flex: 1, gap: 2, minHeight: 0 }}>
        {/* Pie Chart Section */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height={height - (showControls ? 60 : 0)}>
            {renderChart()}
          </ResponsiveContainer>
        </Box>
        
        {/* Category List Section */}
        <Box sx={{ 
          width: '200px', 
          display: 'flex', 
          flexDirection: 'column',
          flexShrink: 0
        }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Categories
          </Typography>
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            },
          }}>
            {chartData.map((item, index) => (
              <Box
                key={item.name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1.5,
                  p: 1,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {/* Color indicator */}
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: COLORS[index % COLORS.length],
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
                
                {/* Category info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(item.value)}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ display: 'block' }}
                  >
                    {item.percentage.toFixed(1)}% • {item.count} items
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          
          {/* Summary at bottom */}
          <Box sx={{ 
            mt: 2, 
            pt: 2, 
            borderTop: 1, 
            borderColor: 'divider',
            flexShrink: 0
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Total: {formatCurrency(totalExpenses)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Transactions: {totalTransactions}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Average: {formatCurrency(averageExpense)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});